import { useOutletContext, useParams, Link } from 'react-router-dom';
import { Card, Radar, Skeleton } from '../../../components/primitives';
import { ChildSubnav } from '../../../components/parent/ChildSubnav';
import type { LinkedChild } from '../../../lib/queries/parent/useLinkedChildren';
import { useChildSummary } from '../../../lib/queries/parent/useChildSummary';
import './Detail.css';

interface OutletShape {
  activeChildId: string | null;
  children: LinkedChild[];
}

const CORNER_LABEL: Record<string, string> = {
  technical_tactical: 'Technical & Tactical',
  physical: 'Physical',
  psychological: 'Psychological',
  social: 'Social',
};

const CORNER_VAR: Record<string, string> = {
  technical_tactical: 'var(--c-tech)',
  physical: 'var(--c-phys)',
  psychological: 'var(--c-psych)',
  social: 'var(--c-social)',
};

/**
 * `/parent/child/:id` — child detail surface.
 *
 * Shows the radar of the latest analytics_snapshot, the active goal
 * cards, and the development plan's parent_summary (the coach's plain-
 * English "what we've been working on"). All three lean on RLS — a
 * parent without a `parent_children` link to this id reads zero rows.
 *
 * Highlights gallery + AI note views ship in a follow-up wave (Wave
 * 3.4b) — they're spec'd in the plan but split for shipping cadence.
 */
export function ChildDetail() {
  const { id: routeId } = useParams<{ id: string }>();
  const { children } = useOutletContext<OutletShape>();
  const playerId = routeId ?? null;
  const { data, loading, error } = useChildSummary(playerId);

  const child = children.find((c) => c.id === playerId) ?? null;

  if (!playerId) {
    return (
      <Card variant="opaque">
        <p>Pick a child from the sidebar to see their detail.</p>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="child-detail" aria-busy="true">
        <ChildSubnav childId={playerId} />
        <header className="parent-page__head">
          <span className="parent-page__eyebrow">{child?.teamName ?? 'Linked child'}</span>
          <h1 className="parent-page__title">{(child?.name ?? 'Your child').split(' ')[0]}'s progress</h1>
          <p className="parent-page__lead">
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="50%" />
          </p>
        </header>
        <Card variant="opaque">
          <div className="child-detail__radar-row">
            <Skeleton variant="circle" width={200} height={200} />
            <div className="child-detail__corner-list">
              <Skeleton variant="text" count={4} width="80%" height={16} />
            </div>
          </div>
        </Card>
        <Card variant="opaque">
          <Skeleton variant="text" width="60%" height={18} />
          <Skeleton variant="text" count={3} width="100%" height={14} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="opaque">
        <p className="parent-page__lead">
          We couldn't load this view. Please try again later.
        </p>
      </Card>
    );
  }

  const corners = data?.cornerScores ?? {};
  const radarValues = {
    tech:   Math.round(corners.technical_tactical ?? 0),
    phys:   Math.round(corners.physical ?? 0),
    psych:  Math.round(corners.psychological ?? 0),
    social: Math.round(corners.social ?? 0),
  };
  const hasAnyScore = Object.values(corners).some((v) => Number(v) > 0);
  const goals = data?.goalCards ?? [];
  const parentSummary = data?.parentSummary ?? null;
  const snapshotDate = data?.snapshotDate ?? null;

  return (
    <div className="child-detail">
      <ChildSubnav childId={playerId} />

      <header className="parent-page__head">
        <span className="parent-page__eyebrow">{child?.teamName ?? 'Linked child'}</span>
        <h1 className="parent-page__title">{(child?.name ?? 'Your child').split(' ')[0]}'s progress</h1>
        <p className="parent-page__lead">
          The four corners of the FA model — Technical &amp; Tactical, Physical,
          Psychological, and Social — show how your child's coach sees their
          development. Updated when the coach saves a session review.
        </p>
      </header>

      <Card variant="opaque">
        <div className="child-detail__radar-row">
          <div className="child-detail__radar-wrap">
            <Radar values={radarValues} size={200} />
          </div>
          <div className="child-detail__corner-list">
            {(['technical_tactical', 'physical', 'psychological', 'social'] as const).map((key) => (
              <div key={key} className="child-detail__corner-row">
                <span
                  className="child-detail__corner-dot"
                  style={{ background: CORNER_VAR[key] }}
                  aria-hidden
                />
                <span className="child-detail__corner-label">{CORNER_LABEL[key]}</span>
                <span className="child-detail__corner-score">
                  {hasAnyScore ? `${Math.round(corners[key] ?? 0)}/100` : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
        {!hasAnyScore && (
          <p className="parent-page__footnote">
            No coach reviews on file yet. Scores appear here once their first
            session review is saved.
          </p>
        )}
        {snapshotDate && (
          <p className="parent-page__footnote">
            Latest update: <strong>{new Date(snapshotDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong>
          </p>
        )}
      </Card>

      <Card variant="opaque">
        <header className="child-detail__section-head">
          <span className="parent-page__eyebrow">From their coach</span>
          <h2 className="child-detail__section-title">What they've been working on</h2>
        </header>
        {parentSummary ? (
          <p className="child-detail__summary-body">{parentSummary}</p>
        ) : (
          <p className="parent-page__lead">
            Your child's coach hasn't shared a summary yet. When they save a
            development plan, the parent-friendly version appears here.
          </p>
        )}
      </Card>

      <Card variant="opaque">
        <header className="child-detail__section-head">
          <span className="parent-page__eyebrow">Active goals</span>
          <h2 className="child-detail__section-title">{goals.length === 0 ? 'No active goals' : `${goals.length} active goal${goals.length === 1 ? '' : 's'}`}</h2>
        </header>
        {goals.length === 0 ? (
          <p className="parent-page__lead">
            Once your child sets a development goal with their coach, it'll
            show up here so you can see what they're aiming for.
          </p>
        ) : (
          <ul className="child-detail__goals">
            {goals.map((g) => (
              <li key={g.id} className="child-detail__goal">
                <span
                  className="child-detail__goal-dot"
                  style={{ background: g.cornerId && CORNER_VAR[g.cornerId.replace(/^corner-/, '')] ? CORNER_VAR[g.cornerId.replace(/^corner-/, '')] : 'var(--fg-3)' }}
                  aria-hidden
                />
                <div className="child-detail__goal-body">
                  <p className="child-detail__goal-text">{g.description}</p>
                  {(g.targetScore != null || g.targetDate) && (
                    <p className="child-detail__goal-meta">
                      {g.targetScore != null && <span>Target: <strong>{g.targetScore}/100</strong></span>}
                      {g.targetScore != null && g.targetDate && <span> · </span>}
                      {g.targetDate && (
                        <span>By {new Date(g.targetDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                      )}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <p className="parent-page__footnote">
        Want to see highlight clips or your child's AI development note?
        Open the <Link to={`/parent/child/${playerId}/highlights`}>Highlights</Link> or{' '}
        <Link to={`/parent/child/${playerId}/ai-note`}>AI note</Link> tab above —
        each is gated by what you consented to in{' '}
        <Link to="/parent/settings">Privacy &amp; consent</Link>.
      </p>
    </div>
  );
}
